// Measure It: guess first, then measure and see. Rulers, a scale and a jug drawn to the
// object's true size, so reading the tool (not guessing) gives the answer.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import { makeLoop } from '@kit/loop';
import type { GameModule, GameContext } from '@shell/types';
import { makeNumpad } from '@games/common/numpad';
import { makeMeasure, goodEye, fmtNum, type MeasureProblem, type ObjKind } from '@problems/measure';

const ITEMS = 8;
type Phase = 'estimate' | 'measure' | 'answer';

function mount(host: HTMLElement, ctx: GameContext) {
  let n = 0;
  let p!: MeasureProblem;
  let phase: Phase = 'estimate';
  let level = ctx.currentLevel();
  let attempts = 0, correct = 0, estimates = 0, goodEyes = 0;
  let misses = 0, hinted = false, locked = false;
  let estimate: number | null = null;
  const t0 = performance.now();
  let tProblem = performance.now();
  // tool state
  let rulerX = 0, objX = 0, pxPer = 10, dragging = false, dragDX = 0;
  let needle = 0, needleTarget = 0, water = 0, waterTarget = 0, measured = false;

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Item ', h('strong', null, '1'), ` of ${ITEMS}`), h('span.muted.small', null, ''));
  const question = h('p.center', { style: { fontWeight: '800', fontSize: '18px', minHeight: '48px', padding: '0 4px' } });
  const stage = h('div');
  const stepLine = h('p.center.muted.small', { style: { minHeight: '20px' } });
  const controls = h('div.stack', { style: { gap: '8px' } });
  host.append(head, question, stage, stepLine, controls);

  const S = makeSurface(stage, { aspect: 0.62, onResize: () => { layoutTool(); draw(); } });
  const loop = makeLoop(update, draw);

  const unPointer = attachPointer(S.canvas, {
    down(pt) {
      if (!(p.kind === 'length' || p.kind === 'compare') || phase === 'estimate') return;
      dragging = true; dragDX = pt.x - rulerX;
    },
    move(pt) { if (dragging) { rulerX = pt.x - dragDX; if (Math.abs(rulerX - objX) < 6) rulerX = objX; draw(); } },
    up() { dragging = false; },
  });

  function newItem() {
    level = ctx.currentLevel();
    p = makeMeasure(level, ctx.rng, n);
    misses = 0; hinted = false; locked = false; estimate = null; measured = false; needle = 0; water = 0;
    tProblem = performance.now();
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    (head.lastElementChild as HTMLElement).textContent = p.kind === 'length' || p.kind === 'compare' ? 'Ruler' : p.kind === 'weight' ? 'Scale' : p.kind === 'capacity' ? 'Jug' : p.kind === 'convert' ? 'Convert' : p.kind === 'scale' ? 'Map' : 'Rate';
    question.textContent = p.question;
    phase = p.kind === 'length' || p.kind === 'weight' || p.kind === 'capacity' ? 'estimate' : 'measure';
    layoutTool();
    renderControls();
    draw();
  }

  function layoutTool() {
    const W = S.w || 380;
    pxPer = Math.min(((W - 40) / p.toolMax), 48);
    if (p.kind === 'length' || p.kind === 'compare') {
      objX = 20 + 10;
      rulerX = objX - (12 + Math.floor(ctx.rng() * 30));
    }
  }

  function renderControls() {
    clear(controls);
    if (phase === 'estimate') {
      stepLine.textContent = 'Step 1: take a guess. Then you will measure to find out.';
      controls.append(h('div.choices', null, ...p.choices.map(c => h('button.choice', { onclick: () => pickEstimate(c) }, fmtNum(c)))));
      return;
    }
    if (phase === 'measure') {
      if (p.kind === 'length' || p.kind === 'compare') {
        stepLine.textContent = 'Step 2: drag the ruler so its 0 lines up with the left end. Then read it.';
        controls.append(h('button.btn.primary.block', { onclick: () => { phase = 'answer'; renderControls(); draw(); } }, 'I can read it'));
      } else if (p.kind === 'weight') {
        stepLine.textContent = 'Step 2: put it on the scale.';
        controls.append(h('button.btn.primary.block', { onclick: () => { measured = true; needleTarget = p.value; loop.start(); setTimeout(() => { phase = 'answer'; renderControls(); }, 700); } }, 'Weigh it'));
      } else if (p.kind === 'capacity') {
        stepLine.textContent = 'Step 2: pour it into the measuring jug.';
        controls.append(h('button.btn.primary.block', { onclick: () => { measured = true; waterTarget = p.value; loop.start(); setTimeout(() => { phase = 'answer'; renderControls(); }, 700); } }, 'Pour it in'));
      } else {
        phase = 'answer'; renderControls();
      }
      return;
    }
    // answer
    if (p.kind === 'compare') {
      stepLine.textContent = 'Which one reaches the bigger number?';
      controls.append(h('div.choices', null, ...p.compareChoices!.map(c => h('button.choice', { style: { fontSize: '18px' }, onclick: (e: Event) => answerCompare(c, e.currentTarget as HTMLElement) }, `the ${c}`))));
      return;
    }
    if (p.useNumpad) {
      stepLine.textContent = p.kind === 'convert' ? `Read the double ruler. ${p.factorText}.` : p.kind === 'scale' ? `Measure in cm, then use ${p.factorText}.` : 'Count the jumps along the track.';
      const pad = makeNumpad({ onEnter: v => answerNumber(v), enterLabel: 'Go' });
      controls.append(pad.el);
      controls.append(h('button.btn.small.ghost.block', { onclick: hint }, 'Hint'));
      return;
    }
    stepLine.textContent = `Step 3: now read the ${p.kind === 'length' ? 'ruler' : p.kind === 'weight' ? 'dial' : 'jug'} and pick the real answer.`;
    controls.append(h('div.choices', null, ...p.choices.map(c => h('button.choice', { onclick: (e: Event) => answerChoice(c, e.currentTarget as HTMLElement) }, fmtNum(c)))));
  }

  function pickEstimate(c: number) {
    estimate = c; estimates++;
    if (goodEye(c, p.answer)) goodEyes++;
    ctx.sound('pop');
    ctx.say(`You guessed ${fmtNum(c)} ${p.unit}. Now measure and see.`);
    phase = 'measure';
    renderControls(); draw();
  }

  function finishRight(msg: string) {
    locked = true;
    attempts++; correct++;
    ctx.report({ skill: 'measure_time_money', level, correct: true, hintUsed: hinted, ms: performance.now() - tProblem, problemId: `measure:${p.kind}:${p.answer}` });
    ctx.sound('ding');
    const eye = estimate !== null ? (goodEye(estimate, p.answer) ? ' Good eye: your guess was close.' : ` Your guess was ${fmtNum(estimate)}; now you know.`) : '';
    ctx.say(msg + eye);
    next();
  }

  function finishWrong(given: string) {
    misses++;
    if (misses === 1) { attempts++; ctx.report({ skill: 'measure_time_money', level, correct: false, hintUsed: hinted, ms: performance.now() - tProblem, problemId: `measure:${p.kind}:${p.answer}` }); }
    ctx.sound('buzz');
    if (misses >= 2) {
      locked = true;
      ctx.say(`It is ${fmtNum(p.answer)} ${p.answerUnit}. ${p.hint}`);
      next(1800);
    } else {
      hinted = true;
      ctx.say(`Not ${given}. Hint: ${p.hint}`);
    }
  }

  function answerChoice(c: number, btn: HTMLElement) {
    if (locked) return;
    if (Math.abs(c - p.answer) < 1e-9) { btn.classList.add('good'); finishRight(`Yes, ${fmtNum(p.answer)} ${p.answerUnit}.`); }
    else { btn.classList.add('bad'); setTimeout(() => btn.classList.remove('bad'), 500); finishWrong(fmtNum(c)); }
  }

  function answerNumber(v: number) {
    if (locked) return;
    if (Math.abs(v - p.answer) < 1e-9) finishRight(`Right: ${fmtNum(p.answer)} ${p.answerUnit}.`);
    else finishWrong(fmtNum(v));
  }

  function answerCompare(c: string, btn: HTMLElement) {
    if (locked) return;
    if (c === p.compareAnswer) { btn.classList.add('good'); finishRight(`Yes, the ${c} is longer: ${fmtNum(Math.max(p.value, p.second!.value))} cm.`); }
    else { btn.classList.add('bad'); setTimeout(() => btn.classList.remove('bad'), 500); finishWrong(`the ${c}`); }
  }

  function hint() {
    hinted = true;
    ctx.say(`Hint: ${p.hint}`);
  }

  function next(delay = 1000) {
    n++;
    if (n >= ITEMS) {
      if (estimates >= 3 && goodEyes === estimates) ctx.charm('charm.ruler');
      setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0, notes: estimates ? [`Guesses within 25%: ${goodEyes} of ${estimates}`] : [] }), delay);
      return;
    }
    setTimeout(newItem, delay);
  }

  function update(dt: number) {
    let moving = false;
    if (measured) {
      if (Math.abs(needle - needleTarget) > 0.01) { needle += (needleTarget - needle) * Math.min(1, dt * 6); moving = true; }
      if (Math.abs(water - waterTarget) > 0.01) { water += (waterTarget - water) * Math.min(1, dt * 5); moving = true; }
    }
    if (!moving) loop.stop();
  }

  // ---------- drawing ----------
  function draw() {
    if (!p || !S.w) return;
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = '#efe9db'; c.fillRect(0, 0, W, H);
    switch (p.kind) {
      case 'length': drawLength(c, W, H); break;
      case 'compare': drawCompare(c, W, H); break;
      case 'weight': drawScale(c, W, H); break;
      case 'capacity': drawJug(c, W, H); break;
      case 'convert': drawConvert(c, W, H); break;
      case 'scale': drawMap(c, W, H); break;
      case 'rate': drawRate(c, W, H); break;
    }
  }

  function drawRuler(c: CanvasRenderingContext2D, x: number, y: number, max: number, tick: number, unit: string, per: number) {
    const w = max * per;
    c.fillStyle = '#f5c542'; roundRect(c, x, y, w, 34, 4); c.fill();
    c.strokeStyle = '#2a2a2e'; c.lineWidth = 1.5;
    c.fillStyle = '#2a2a2e'; c.font = '700 10px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'top';
    const whole = tick < 1 ? 1 : tick;
    for (let v = 0; v <= max + 1e-9; v += tick) {
      const px = x + v * per;
      const isWhole = Math.abs(v / whole - Math.round(v / whole)) < 1e-9;
      c.beginPath(); c.moveTo(px, y); c.lineTo(px, y + (isWhole ? 14 : 8)); c.stroke();
      if (isWhole && (max <= 30 || Math.round(v) % (whole * 2) === 0)) c.fillText(String(Math.round(v)), px, y + 17);
    }
    c.font = '800 10px Nunito, sans-serif'; c.textAlign = 'right'; c.fillText(unit, x + w - 2, y + 24);
  }

  function drawObject(c: CanvasRenderingContext2D, kind: ObjKind, x: number, y: number, len: number, hgt: number) {
    switch (kind) {
      case 'pencil': c.fillStyle = '#f5c542'; c.fillRect(x + len * 0.08, y, len * 0.84, hgt); c.fillStyle = '#e9b995'; c.beginPath(); c.moveTo(x + len * 0.92, y); c.lineTo(x + len, y + hgt / 2); c.lineTo(x + len * 0.92, y + hgt); c.fill(); c.fillStyle = '#ee86b7'; c.fillRect(x, y, len * 0.08, hgt); c.fillStyle = '#2a2a2e'; c.beginPath(); c.moveTo(x + len * 0.97, y + hgt * 0.3); c.lineTo(x + len, y + hgt / 2); c.lineTo(x + len * 0.97, y + hgt * 0.7); c.fill(); break;
      case 'crayon': c.fillStyle = '#e2574c'; c.fillRect(x, y, len * 0.9, hgt); c.beginPath(); c.moveTo(x + len * 0.9, y); c.lineTo(x + len, y + hgt / 2); c.lineTo(x + len * 0.9, y + hgt); c.fill(); c.fillStyle = 'rgba(0,0,0,.15)'; c.fillRect(x + len * 0.1, y, len * 0.7, hgt * 0.35); break;
      case 'spoon': c.fillStyle = '#9a9aa3'; c.fillRect(x + len * 0.3, y + hgt * 0.35, len * 0.7, hgt * 0.3); c.beginPath(); c.ellipse(x + len * 0.17, y + hgt / 2, len * 0.17, hgt * 0.55, 0, 0, 7); c.fill(); break;
      case 'book': c.fillStyle = '#4f8fe6'; roundRect(c, x, y, len, hgt, 3); c.fill(); c.fillStyle = '#f6f3ec'; c.fillRect(x + 4, y + hgt * 0.3, len - 8, 4); c.fillRect(x + 4, y + hgt * 0.55, len * 0.6, 4); break;
      case 'shoe': c.fillStyle = '#9b6be6'; c.beginPath(); c.moveTo(x, y + hgt); c.lineTo(x, y + hgt * 0.4); c.quadraticCurveTo(x + len * 0.2, y, x + len * 0.45, y + hgt * 0.1); c.lineTo(x + len * 0.65, y + hgt * 0.5); c.lineTo(x + len, y + hgt * 0.6); c.lineTo(x + len, y + hgt); c.closePath(); c.fill(); c.fillStyle = '#f6f3ec'; c.fillRect(x, y + hgt * 0.85, len, hgt * 0.15); break;
      case 'carrot': c.fillStyle = '#f08a3c'; c.beginPath(); c.moveTo(x, y + hgt / 2); c.lineTo(x + len * 0.85, y); c.lineTo(x + len * 0.85, y + hgt); c.closePath(); c.fill(); c.fillStyle = '#5fbf6a'; c.fillRect(x + len * 0.85, y + hgt * 0.2, len * 0.15, hgt * 0.2); c.fillRect(x + len * 0.85, y + hgt * 0.55, len * 0.12, hgt * 0.2); break;
      case 'ribbon': c.fillStyle = '#ee86b7'; c.fillRect(x, y + hgt * 0.3, len, hgt * 0.4); c.fillStyle = '#e46191'; c.beginPath(); c.moveTo(x, y + hgt * 0.3); c.lineTo(x + hgt * 0.3, y + hgt / 2); c.lineTo(x, y + hgt * 0.7); c.fill(); break;
      case 'stick': c.fillStyle = '#8a5a3b'; roundRect(c, x, y + hgt * 0.3, len, hgt * 0.4, 3); c.fill(); c.fillStyle = '#5a3a1e'; c.fillRect(x + len * 0.4, y + hgt * 0.1, hgt * 0.25, hgt * 0.3); break;
      case 'road': c.fillStyle = '#9a9aa3'; c.fillRect(x, y, len, hgt); c.fillStyle = '#f6f3ec'; for (let d = 0; d < len - 6; d += 14) c.fillRect(x + d + 3, y + hgt / 2 - 1.5, 8, 3); break;
      case 'river': c.fillStyle = '#4f8fe6'; c.beginPath(); c.moveTo(x, y + hgt * 0.5); for (let d = 0; d <= len; d += 6) c.lineTo(x + d, y + hgt * 0.5 + Math.sin(d / 9) * hgt * 0.3); c.lineTo(x + len, y + hgt); c.lineTo(x, y + hgt); c.closePath(); c.fill(); break;
      default: c.fillStyle = '#b48a5a'; roundRect(c, x, y, len, hgt, 4); c.fill();
    }
  }

  function drawBigObject(c: CanvasRenderingContext2D, kind: ObjKind, cx: number, cy: number, s: number) {
    switch (kind) {
      case 'apple': c.fillStyle = '#e2574c'; c.beginPath(); c.arc(cx, cy, s, 0, 7); c.fill(); c.fillStyle = '#8a5a3b'; c.fillRect(cx - 2, cy - s - 8, 4, 10); c.fillStyle = '#5fbf6a'; c.beginPath(); c.ellipse(cx + 8, cy - s - 4, 8, 4, 0.5, 0, 7); c.fill(); break;
      case 'cat': c.fillStyle = '#f08a3c'; c.beginPath(); c.ellipse(cx, cy + s * 0.2, s, s * 0.7, 0, 0, 7); c.fill(); c.beginPath(); c.arc(cx + s * 0.7, cy - s * 0.4, s * 0.45, 0, 7); c.fill(); c.beginPath(); c.moveTo(cx + s * 0.4, cy - s * 0.6); c.lineTo(cx + s * 0.5, cy - s * 1.1); c.lineTo(cx + s * 0.7, cy - s * 0.75); c.fill(); c.beginPath(); c.moveTo(cx + s * 0.8, cy - s * 0.75); c.lineTo(cx + s * 0.95, cy - s * 1.1); c.lineTo(cx + s, cy - s * 0.6); c.fill(); break;
      case 'dog': c.fillStyle = '#b48a5a'; c.beginPath(); c.ellipse(cx, cy + s * 0.2, s * 1.1, s * 0.65, 0, 0, 7); c.fill(); c.beginPath(); c.arc(cx + s * 0.9, cy - s * 0.3, s * 0.45, 0, 7); c.fill(); c.fillStyle = '#8a5a3b'; c.beginPath(); c.ellipse(cx + s * 0.6, cy - s * 0.35, s * 0.18, s * 0.35, 0.4, 0, 7); c.fill(); c.fillStyle = '#2a2a2e'; c.beginPath(); c.arc(cx + s * 1.3, cy - s * 0.3, 3, 0, 7); c.fill(); break;
      case 'melon': c.fillStyle = '#5fbf6a'; c.beginPath(); c.arc(cx, cy, s, 0, 7); c.fill(); c.strokeStyle = '#2e7d4f'; c.lineWidth = 3; for (let i = -2; i <= 2; i++) { c.beginPath(); c.moveTo(cx + i * s * 0.35, cy - s * 0.9); c.quadraticCurveTo(cx + i * s * 0.5, cy, cx + i * s * 0.35, cy + s * 0.9); c.stroke(); } break;
      case 'backpack': c.fillStyle = '#4f8fe6'; roundRect(c, cx - s * 0.8, cy - s, s * 1.6, s * 2, s * 0.3); c.fill(); c.fillStyle = '#2d5fb8'; roundRect(c, cx - s * 0.5, cy, s, s * 0.7, 6); c.fill(); c.fillStyle = '#f5c542'; c.fillRect(cx - s * 0.1, cy - s * 0.9, s * 0.2, s * 0.5); break;
      case 'rock': c.fillStyle = '#8c8a86'; c.beginPath(); c.moveTo(cx - s, cy + s * 0.5); c.lineTo(cx - s * 0.6, cy - s * 0.6); c.lineTo(cx + s * 0.3, cy - s); c.lineTo(cx + s, cy - s * 0.1); c.lineTo(cx + s * 0.6, cy + s * 0.7); c.closePath(); c.fill(); break;
      case 'cup': c.fillStyle = '#f6f3ec'; roundRect(c, cx - s * 0.6, cy - s * 0.7, s * 1.2, s * 1.5, 6); c.fill(); c.strokeStyle = '#f6f3ec'; c.lineWidth = 6; c.beginPath(); c.arc(cx + s * 0.75, cy, s * 0.35, -1.2, 1.2); c.stroke(); break;
      case 'bottle': c.fillStyle = '#3bb3a9'; roundRect(c, cx - s * 0.45, cy - s * 0.6, s * 0.9, s * 1.8, 8); c.fill(); c.fillRect(cx - s * 0.2, cy - s * 1.1, s * 0.4, s * 0.55); c.fillStyle = '#2a2a2e'; c.fillRect(cx - s * 0.25, cy - s * 1.25, s * 0.5, s * 0.18); break;
      case 'bucket': c.fillStyle = '#9a9aa3'; c.beginPath(); c.moveTo(cx - s, cy - s * 0.8); c.lineTo(cx + s, cy - s * 0.8); c.lineTo(cx + s * 0.75, cy + s); c.lineTo(cx - s * 0.75, cy + s); c.closePath(); c.fill(); c.strokeStyle = '#6d6b67'; c.lineWidth = 4; c.beginPath(); c.arc(cx, cy - s * 0.8, s, Math.PI, 0); c.stroke(); break;
      case 'jug': c.fillStyle = '#bfe8ff'; roundRect(c, cx - s * 0.7, cy - s * 0.9, s * 1.4, s * 1.9, 8); c.fill(); c.fillStyle = '#4f8fe6'; c.fillRect(cx - s * 0.7, cy - s * 0.2, s * 1.4, s * 1.2); c.strokeStyle = '#bfe8ff'; c.lineWidth = 6; c.beginPath(); c.arc(cx + s * 0.9, cy, s * 0.4, -1.3, 1.3); c.stroke(); break;
      default: drawObject(c, kind, cx - s, cy - s * 0.3, s * 2, s * 0.6);
    }
  }

  function drawLength(c: CanvasRenderingContext2D, W: number, H: number) {
    const len = p.value * pxPer;
    drawObject(c, p.object, objX, H * 0.3, len, Math.min(28, H * 0.14));
    if (phase !== 'estimate') drawRuler(c, rulerX, H * 0.55, p.toolMax, p.tick, p.unit, pxPer);
    else { c.fillStyle = '#6a6a72'; c.font = '700 13px Nunito, sans-serif'; c.textAlign = 'center'; c.fillText('Guess first. The ruler comes next.', W / 2, H * 0.72); }
    if (phase !== 'estimate' && Math.abs(rulerX - objX) > 0.5) { c.fillStyle = '#e2574c'; c.font = '800 12px Nunito, sans-serif'; c.textAlign = 'left'; c.fillText('line up the 0 with the end', rulerX + 4, H * 0.55 + 46); }
  }

  function drawCompare(c: CanvasRenderingContext2D, W: number, H: number) {
    drawObject(c, p.object, objX, H * 0.12, p.value * pxPer, Math.min(24, H * 0.12));
    drawObject(c, p.second!.object, objX, H * 0.36, p.second!.value * pxPer, Math.min(24, H * 0.12));
    c.fillStyle = '#2a2a2e'; c.font = '700 12px Nunito, sans-serif'; c.textAlign = 'left';
    c.fillText(p.objectName, objX, H * 0.09); c.fillText(p.second!.objectName, objX, H * 0.33);
    drawRuler(c, rulerX, H * 0.6, p.toolMax, p.tick, p.unit, pxPer);
    void W;
  }

  function drawScale(c: CanvasRenderingContext2D, W: number, H: number) {
    const cx = W / 2, cy = H * 0.62, r = Math.min(W * 0.36, H * 0.5);
    // dial
    c.fillStyle = '#f6f3ec'; c.beginPath(); c.arc(cx, cy, r, Math.PI, 0); c.lineTo(cx + r, cy + 12); c.lineTo(cx - r, cy + 12); c.closePath(); c.fill();
    c.strokeStyle = '#2a2a2e'; c.lineWidth = 2; c.stroke();
    const steps = Math.round(p.toolMax / p.tick);
    c.fillStyle = '#2a2a2e'; c.font = '700 10px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
    for (let i = 0; i <= steps; i++) {
      const a = Math.PI + (i / steps) * Math.PI;
      const big = steps <= 10 || i % 2 === 0;
      c.beginPath(); c.moveTo(cx + Math.cos(a) * r * 0.9, cy + Math.sin(a) * r * 0.9); c.lineTo(cx + Math.cos(a) * r * (big ? 0.78 : 0.84), cy + Math.sin(a) * r * (big ? 0.78 : 0.84)); c.stroke();
      if (big && (steps <= 10 || i % 4 === 0)) c.fillText(String(Math.round(i * p.tick)), cx + Math.cos(a) * r * 0.66, cy + Math.sin(a) * r * 0.66);
    }
    c.font = '800 11px Nunito, sans-serif'; c.fillText(p.unit, cx, cy - r * 0.3);
    // needle
    const na = Math.PI + (needle / p.toolMax) * Math.PI;
    c.strokeStyle = '#e2574c'; c.lineWidth = 4; c.lineCap = 'round';
    c.beginPath(); c.moveTo(cx, cy); c.lineTo(cx + Math.cos(na) * r * 0.85, cy + Math.sin(na) * r * 0.85); c.stroke();
    c.fillStyle = '#2a2a2e'; c.beginPath(); c.arc(cx, cy, 6, 0, 7); c.fill();
    // pan + object
    c.fillStyle = '#9a9aa3'; c.fillRect(cx - r * 0.5, cy + 14, r, 8);
    drawBigObject(c, p.object, measured ? cx : W * 0.15, measured ? cy - r - 28 : H * 0.3, Math.min(26, r * 0.28));
  }

  function drawJug(c: CanvasRenderingContext2D, W: number, H: number) {
    const jx = W * 0.55, jy = H * 0.12, jw = W * 0.3, jh = H * 0.76;
    // water
    const frac = Math.min(1, water / p.toolMax);
    c.fillStyle = '#4f8fe6'; c.fillRect(jx, jy + jh * (1 - frac), jw, jh * frac);
    // jug outline and lines
    c.strokeStyle = '#2a2a2e'; c.lineWidth = 3; c.strokeRect(jx, jy, jw, jh);
    const steps = Math.round(p.toolMax / p.tick);
    c.fillStyle = '#2a2a2e'; c.font = '700 10px Nunito, sans-serif'; c.textAlign = 'right'; c.textBaseline = 'middle';
    for (let i = 1; i <= steps; i++) {
      const y = jy + jh * (1 - i / steps);
      c.beginPath(); c.moveTo(jx, y); c.lineTo(jx + (i % 2 === 0 || steps <= 10 ? 16 : 9), y); c.stroke();
      if (steps <= 10 || i % 2 === 0) c.fillText(String(Math.round(i * p.tick)), jx - 4, y);
    }
    c.textAlign = 'left'; c.font = '800 11px Nunito, sans-serif'; c.fillText(p.unit, jx + jw + 6, jy + 8);
    drawBigObject(c, p.object, W * 0.22, H * 0.5, Math.min(30, W * 0.08));
    if (measured) { c.fillStyle = '#4f8fe6'; c.fillRect(W * 0.3, H * 0.3, jx - W * 0.3, 4); }
  }

  function drawConvert(c: CanvasRenderingContext2D, W: number, H: number) {
    const steps = Math.round(p.toolMax / p.tick);
    const x = 20, w = W - 40, per = w / steps;
    const y = H * 0.4;
    c.fillStyle = '#f5c542'; roundRect(c, x, y - 22, w, 44, 4); c.fill();
    c.strokeStyle = '#2a2a2e'; c.lineWidth = 1.5; c.fillStyle = '#2a2a2e'; c.font = '700 11px Nunito, sans-serif'; c.textAlign = 'center';
    const factor = p.answerUnit === 'cm' && p.unit === 'm' ? 100 : p.answerUnit === 'm' ? 1 / 100 : p.answerUnit === 'ft' ? 1 / 12 : p.answerUnit === 'in' ? 12 : 1000;
    for (let i = 0; i <= steps; i++) {
      const px = x + i * per;
      c.beginPath(); c.moveTo(px, y - 22); c.lineTo(px, y + 22); c.stroke();
      c.textBaseline = 'bottom'; c.fillText(String(Math.round(i * p.tick * 100) / 100), px, y - 26);
      c.textBaseline = 'top'; c.fillText(String(Math.round(i * p.tick * factor * 100) / 100), px, y + 26);
    }
    c.font = '800 12px Nunito, sans-serif'; c.textAlign = 'left'; c.textBaseline = 'alphabetic';
    c.fillText(p.unit, x, y - 44); c.fillText(p.answerUnit, x, y + 56);
    // highlight the asked value
    const vx = x + (p.value / p.tick) * per;
    c.fillStyle = '#e2574c'; c.beginPath(); c.arc(vx, y, 7, 0, 7); c.fill();
    c.fillStyle = '#2a2a2e'; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'center'; c.fillText(`${fmtNum(p.value)} ${p.unit} is here`, Math.min(W - 60, Math.max(60, vx)), H * 0.85);
  }

  function drawMap(c: CanvasRenderingContext2D, W: number, H: number) {
    c.fillStyle = '#cfe6b3'; c.fillRect(0, 0, W, H);
    c.fillStyle = '#9fd3e8'; c.beginPath(); c.ellipse(W * 0.8, H * 0.25, W * 0.18, H * 0.15, 0, 0, 7); c.fill();
    const len = p.value * pxPer;
    drawObject(c, p.object, 30, H * 0.35, len, 14);
    drawRuler(c, 30, H * 0.55, p.toolMax, 1, 'cm', pxPer);
    c.fillStyle = '#2a2a2e'; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'left'; c.textBaseline = 'alphabetic';
    c.fillText(`Map scale: ${p.factorText}`, 30, H * 0.2);
  }

  function drawRate(c: CanvasRenderingContext2D, W: number, H: number) {
    const per = (W - 40) / p.toolMax;
    drawRuler(c, 20, H * 0.5, p.toolMax, p.tick, 'cm', per);
    c.fillStyle = '#2a2a2e'; c.font = '800 12px Nunito, sans-serif'; c.textAlign = 'left'; c.textBaseline = 'alphabetic';
    c.fillText(`Each mark is one minute (${fmtNum(p.value)} cm).`, 20, H * 0.3);
    // the mover at the start
    c.fillStyle = '#5fbf6a'; c.beginPath(); c.arc(20, H * 0.42, 8, 0, 7); c.fill();
    c.fillStyle = '#2a2a2e'; c.beginPath(); c.arc(24, H * 0.4, 1.5, 0, 7); c.fill();
  }

  newItem();
  return { destroy() { loop.stop(); unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.measure-it',
  title: 'Measure It',
  blurb: 'Guess the length, weight or amount, then measure to see how close you were.',
  category: 'math',
  skills: ['measure_time_money'],
  levelRange: [1, 10],
  unlock: { stars: 13 },
  mount,
};
export default mod;
